import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";
import { IMAGES, BRAND } from "@/site";
import { PageHero, Section } from "@/components/ui-blocks/Blocks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Training services",
  "Pre-trip inspection support",
  "Refresher training",
  "Corrective training",
  "District support",
  "CLB Solution Pretrip Guide",
  "Other / future services",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    service_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.organization) delete payload.organization;
      if (!payload.role) delete payload.role;
      if (!payload.service_interest) delete payload.service_interest;
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you — your inquiry has been received.");
      setForm({ name: "", email: "", organization: "", role: "", service_interest: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(Array.isArray(msg) ? "Please check your form fields." : String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        testid="contact-hero"
        eyebrow="Contact"
        title="Let's talk training."
        description="For training inquiries, district partnerships, future services, or digital tools, contact CLB Solution directly."
        image={IMAGES.heroHome}
      />

      <Section testid="contact-section">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-white/10 bg-[#0c0c0e] p-7">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 font-semibold">Email</div>
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link" className="font-display text-xl md:text-2xl font-bold uppercase break-words hover:text-[#FFCC00] transition-colors">
                {BRAND.email}
              </a>
              <div className="mt-2 text-sm text-white/60 flex items-center gap-2"><Mail size={14}/> Direct inquiry</div>
            </div>
            <div className="border border-white/10 bg-[#0c0c0e] p-7">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 font-semibold">Business Location</div>
              <div className="font-display text-xl md:text-2xl font-bold uppercase">Delaware-based</div>
              <div className="mt-2 text-sm text-white/60 flex items-center gap-2"><MapPin size={14}/> Launching in Delaware with future expansion planned.</div>
            </div>
            <div className="border border-white/10 bg-[#0c0c0e] p-7">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 font-semibold">What to Include</div>
              <ul className="space-y-2 text-sm text-white/80">
                <li>• Your name</li>
                <li>• Organization, if applicable</li>
                <li>• Your role</li>
                <li>• Service needed</li>
                <li>• Your questions and goals</li>
              </ul>
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-7 border border-white/10 bg-[#0c0c0e] p-7 md:p-10 space-y-6">
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">Send a message</h3>

            <Field label="Name *" testid="contact-name">
              <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} className="w-full bg-[#09090b] border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]" placeholder="Your full name" />
            </Field>

            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Email *" testid="contact-email-field">
                <input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className="w-full bg-[#09090b] border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]" placeholder="you@example.com" />
              </Field>
              <Field label="Organization" testid="contact-org">
                <input data-testid="contact-input-org" value={form.organization} onChange={update("organization")} className="w-full bg-[#09090b] border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]" placeholder="District, contractor, or company" />
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Your Role" testid="contact-role">
                <input data-testid="contact-input-role" value={form.role} onChange={update("role")} className="w-full bg-[#09090b] border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]" placeholder="Trainee, Trainer, Supervisor…" />
              </Field>
              <Field label="Service Interest" testid="contact-interest">
                <select data-testid="contact-input-interest" value={form.service_interest} onChange={update("service_interest")} className="w-full bg-[#09090b] border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00]">
                  <option value="">— Select —</option>
                  {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Message *" testid="contact-message">
              <textarea data-testid="contact-input-message" required value={form.message} onChange={update("message")} rows={6} className="w-full bg-[#09090b] border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-[#FFCC00] resize-y" placeholder="Tell us about your questions and goals…" />
            </Field>

            <button
              data-testid="contact-submit"
              disabled={submitting}
              type="submit"
              className="inline-flex items-center gap-2 bg-[#FFCC00] text-black font-bold uppercase tracking-[0.18em] text-xs px-7 py-4 hover:bg-[#E6B800] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Send Inquiry"} <Send size={14} />
            </button>

            <p className="text-xs text-white/40 leading-relaxed">
              By submitting, you agree your message will be stored in CLB Solution&apos;s contact log
              and used solely to respond to your inquiry.
            </p>
          </form>
        </div>
      </Section>
    </>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-white/60 mb-2 font-semibold">{label}</span>
      {children}
    </label>
  );
}
